<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ContactPicker from 'dashboard/components-next/CRM/Documents/ContactPicker/ContactPicker.vue';
import ChannelPicker from 'dashboard/components-next/CRM/Documents/ChannelPicker/ChannelPicker.vue';

const props = defineProps({ isSubmitting: { type: Boolean, default: false } });
const emit = defineEmits(['create']);
const { t } = useI18n();

const dialogRef = ref(null);
const contactPickerRef = ref(null);
const form = ref({
  contactId: '',
  contactInboxId: '',
  message: '',
});

const rules = {
  contactId: { required },
};

const v$ = useVuelidate(rules, form);

const reset = () => {
  form.value = { contactId: '', contactInboxId: '', message: '' };
  v$.value.$reset();
};

const open = () => {
  reset();
  contactPickerRef.value?.load();
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const submit = async () => {
  if (props.isSubmitting) return;

  const isValid = await v$.value.$validate();
  if (!isValid) return;

  emit('create', {
    contactId: form.value.contactId,
    contactInboxId: form.value.contactInboxId,
    message: form.value.message.trim(),
  });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('DOCUMENTS.SIGNATURES.DIALOG.TITLE')"
    :description="t('DOCUMENTS.SIGNATURES.DIALOG.DESCRIPTION')"
    width="lg"
    :persistent="isSubmitting"
  >
    <div class="flex flex-col gap-4 py-4">
      <div>
        <ContactPicker
          ref="contactPickerRef"
          v-model="form.contactId"
          :label="t('DOCUMENTS.SIGNATURES.DIALOG.SIGNER')"
          :placeholder="t('DOCUMENTS.SIGNATURES.DIALOG.SIGNER_PLACEHOLDER')"
          :has-error="v$.contactId.$error"
        />
        <span v-if="v$.contactId.$error" class="text-xs text-n-ruby-11 mt-1 block">
          {{ t('DOCUMENTS.SIGNATURES.DIALOG.SIGNER_REQUIRED') }}
        </span>
      </div>
      <ChannelPicker
        v-model="form.contactInboxId"
        :contact-id="form.contactId"
        :label="t('DOCUMENTS.CHANNEL_PICKER.LABEL')"
      />
      <TextArea
        v-model="form.message"
        :label="t('DOCUMENTS.SIGNATURES.DIALOG.MESSAGE')"
        :placeholder="t('DOCUMENTS.SIGNATURES.DIALOG.MESSAGE_PLACEHOLDER')"
      />
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          :label="t('DOCUMENTS.CANCEL')"
          ghost
          slate
          :disabled="isSubmitting"
          @click="close"
        />
        <Button
          :label="t('DOCUMENTS.SIGNATURES.DIALOG.SUBMIT')"
          :is-loading="isSubmitting"
          :disabled="isSubmitting"
          @click="submit"
        />
      </div>
    </template>
  </Dialog>
</template>
